import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, Crown, Music2, ShieldOff, BadgeCheck, Headphones, Mic } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const LISTENER_FEATURES = [
  { icon: Music2, label: "Unlimited playlists & libraries" },
  { icon: ShieldOff, label: "Zero ads — pure music" },
  { icon: BadgeCheck, label: "Premium badge on your profile" },
  { icon: Headphones, label: "High-quality preview tier" },
  { icon: Sparkles, label: "Early access to new features" },
];

const ARTIST_FEATURES = [
  { icon: Mic, label: "Artist Studio — upload your own MP3s" },
  { icon: Sparkles, label: "Public artist profile + bio" },
  { icon: BadgeCheck, label: "Verified Artist badge" },
  { icon: Music2, label: "Everything in Listener Premium" },
  { icon: Headphones, label: "Early eligibility for AI Release Tracker" },
];

// Kept for backward compat with any external imports
const FEATURES = LISTENER_FEATURES;

const Premium = () => {
  const { user, login, premium: globalPremium, refreshPremium } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [params, setParams] = useSearchParams();

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return;
    }
    const r = await refreshPremium();
    if (r) setStatus(r);
  }, [user, refreshPremium]);

  useEffect(() => {
    if (globalPremium) setStatus(globalPremium);
  }, [globalPremium]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll after stripe redirects back with session_id
  useEffect(() => {
    const session_id = params.get("session_id");
    const cancelled = params.get("status") === "cancelled";
    if (cancelled) {
      toast.info("Payment cancelled");
      setParams({}, { replace: true });
      return;
    }
    if (!session_id || polling) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/premium/checkout/status/${session_id}`);
        if (r.data.payment_status === "paid") {
          toast.success("🎉 Welcome to Premium!");
          setStatus(r.data.premium);
          refreshPremium();
          setParams({}, { replace: true });
          setPolling(false);
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Payment session expired");
          setParams({}, { replace: true });
          setPolling(false);
          return;
        }
      } catch {
        /* keep trying */
      }
      if (attempts < 6) {
        setTimeout(tick, 2000);
      } else {
        toast.message("Payment status pending — check back shortly");
        setPolling(false);
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startTrial = async (plan = "premium") => {
    if (!user) {
      login();
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/premium/start-trial", { plan });
      setStatus(r.data);
      refreshPremium();
      const days = r.data?.trial_days_granted || (plan === "artist" ? 90 : 60);
      const months = Math.round(days / 30);
      toast.success(`Your ${months}-month free trial has started!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (plan = "premium") => {
    if (!user) {
      login();
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/premium/checkout", { origin_url: window.location.origin, plan });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const isPremiumActive = status?.active;
  const tier = status?.tier || "free";

  // No-op placeholder for /premium?plan=artist deep-link from /upload (Artist card is already prominent).
  // Reserved here in case we want to auto-scroll to it on small screens later.
  useEffect(() => {
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [params]);

  return (
    <div data-testid="premium-page" className="pb-16">
      <div className="relative -mx-4 sm:-mx-8 -mt-8 px-4 sm:px-8 py-16 mb-10 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background: `radial-gradient(900px circle at 30% 30%, rgba(255,51,102,0.35), transparent 60%), radial-gradient(700px circle at 75% 70%, rgba(157,76,221,0.3), transparent 60%), radial-gradient(500px circle at 50% 50%, rgba(226,255,49,0.12), transparent 60%), #050505`,
          }}
        />
        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-3 flex items-center gap-2">
          <Crown size={14} className="text-[#E2FF31]" /> 1o1sound Premium
        </p>
        <h1 className="font-display text-5xl sm:text-7xl font-semibold tracking-tighter leading-[0.95] max-w-3xl">
          Months <span className="bg-gradient-to-r from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] bg-clip-text text-transparent">on us.</span>
        </h1>
        <p className="text-white/70 mt-5 text-lg max-w-xl">
          <span className="text-white font-semibold">Listeners</span> — 2 months free, then $10/month.
          <br />
          <span className="text-white font-semibold">Artists</span> — <span className="bg-[#E2FF31]/20 text-[#E2FF31] px-1.5 py-0.5 rounded">3 months free</span>, then $25/month. Cancel any time.
          <br />
          <span className="text-[11px] uppercase tracking-wider text-white/45 mt-2 inline-block">Each plan has its own free trial — try both</span>
        </p>
      </div>

      {/* Status card */}
      {user && status && (
        <div data-testid="premium-status" className="mb-8 p-5 rounded-2xl bg-white/[0.04] border border-white/10 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPremiumActive ? "bg-gradient-to-br from-[#FF2E00] to-[#E2FF31]" : "bg-white/10"}`}>
              <Crown size={18} className={isPremiumActive ? "text-black" : "text-white/50"} />
            </div>
            <div>
              <p className="text-sm font-semibold capitalize">
                {tier === "trial"
                  ? "Free Trial Active"
                  : tier === "artist"
                  ? "Artist Premium Active"
                  : tier === "premium"
                  ? "Listener Premium Active"
                  : "Free Plan"}
              </p>
              <p className="text-xs text-white/50">
                {isPremiumActive && status.days_left > 0
                  ? `${status.days_left} day${status.days_left === 1 ? "" : "s"} left`
                  : status.trial_used ? "Trial already used" : "Not subscribed"}
              </p>
            </div>
          </div>
          {isPremiumActive && (
            <button
              data-testid="premium-manage-btn"
              onClick={() => navigate("/account")}
              className="text-sm bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition"
            >
              Manage subscription →
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
        {/* Listener Premium card */}
        <div
          data-testid="premium-tier-listener"
          className="relative rounded-3xl overflow-hidden p-7 border border-white/10 bg-gradient-to-br from-[#FF2E00]/12 to-[#FF2E00]/8"
        >
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FF2E00]/25 blur-3xl" />
          <div className="relative">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/55 font-bold mb-1">For Listeners</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight">Listener Premium</h2>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-5xl font-semibold">$10</span>
              <span className="text-white/55">/month</span>
            </div>
            <p className="text-sm text-white/60 mt-1">After your 2-month free trial</p>

            <ul className="mt-6 space-y-2.5">
              {LISTENER_FEATURES.map((f) => (
                <li key={f.label} className="flex items-center gap-2.5 text-sm">
                  <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <f.icon size={13} className="text-white/80" />
                  </span>
                  <span className="text-white/85">{f.label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-col gap-2">
              {!user ? (
                <button
                  data-testid="premium-signin-btn"
                  onClick={login}
                  className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.02] transition"
                >
                  Sign in to start trial
                </button>
              ) : tier === "premium" ? (
                <>
                  <button
                    disabled
                    data-testid="premium-listener-current-btn"
                    className="bg-white/15 text-white rounded-full px-6 py-2.5 cursor-default flex items-center justify-center gap-2"
                  >
                    <Check size={16} className="text-[#E2FF31]" /> Your current plan
                  </button>
                  {!status?.trial_used_artist ? (
                    <button
                      data-testid="premium-switch-to-artist-trial-btn"
                      onClick={() => startTrial("artist")}
                      disabled={loading}
                      className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Sparkles size={14} className="text-[#E2FF31]" /> Try Artist free · 3 months
                    </button>
                  ) : (
                    <button
                      data-testid="premium-switch-to-artist-btn"
                      onClick={() => subscribe("artist")}
                      disabled={loading}
                      className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Sparkles size={14} className="text-[#E2FF31]" /> Switch to Artist · $25/mo
                    </button>
                  )}
                </>
              ) : tier === "artist" ? (
                !status?.trial_used_listener ? (
                  <button
                    data-testid="premium-switch-to-listener-trial-btn"
                    onClick={() => startTrial("premium")}
                    disabled={loading}
                    className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    ↓ Try Listener free · 2 months
                  </button>
                ) : (
                  <button
                    data-testid="premium-switch-to-listener-btn"
                    onClick={() => subscribe("premium")}
                    disabled={loading}
                    className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    ↓ Switch to Listener · $10/mo
                  </button>
                )
              ) : !status?.trial_used_listener && !isPremiumActive ? (
                <>
                  <button
                    data-testid="premium-start-trial-btn"
                    onClick={() => startTrial("premium")}
                    disabled={loading}
                    className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.02] transition disabled:opacity-50"
                  >
                    {loading ? "Starting…" : "Start 2-month free trial"}
                  </button>
                  <button
                    data-testid="premium-subscribe-listener-btn"
                    onClick={() => subscribe("premium")}
                    disabled={loading}
                    className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition text-sm"
                  >
                    Skip trial — $10/month
                  </button>
                </>
              ) : (
                <button
                  data-testid="premium-subscribe-listener-btn"
                  onClick={() => subscribe("premium")}
                  disabled={loading}
                  className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.02] transition disabled:opacity-50"
                >
                  Subscribe · $10/month
                </button>
              )}
            </div>
            <p className="text-xs text-white/40 mt-3">Cancel any time. Trial available once per account.</p>
          </div>
        </div>

        {/* Artist Premium card */}
        <div
          data-testid="premium-tier-artist"
          className="relative rounded-3xl overflow-hidden p-7 border-2 border-[#FF2E00]/40 bg-gradient-to-br from-[#FF2E00]/15 via-[#FF2E00]/12 to-[#E2FF31]/8 shadow-[0_0_40px_-10px_rgba(255,51,102,0.4)]"
        >
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#FF2E00]/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-[#FF2E00]/30 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#E2FF31] font-bold">For Artists</p>
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#E2FF31]/20 text-[#E2FF31] border border-[#E2FF31]/30">
                <Sparkles size={9} className="inline mr-1" />Best for creators
              </span>
            </div>
            <h2 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2">
              <Mic size={22} className="text-[#FF2E00]" /> Artist Premium
            </h2>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-5xl font-semibold">$25</span>
              <span className="text-white/55">/month</span>
            </div>
            <p className="text-sm text-white/60 mt-1">
              <span className="text-[#E2FF31] font-semibold">3 months free</span> · upload + monetize your music
            </p>

            <ul className="mt-6 space-y-2.5">
              {ARTIST_FEATURES.map((f) => (
                <li key={f.label} className="flex items-center gap-2.5 text-sm">
                  <span className="w-7 h-7 rounded-full bg-[#FF2E00]/20 flex items-center justify-center shrink-0">
                    <f.icon size={13} className="text-[#E2FF31]" />
                  </span>
                  <span className="text-white/90">{f.label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-col gap-2">
              {!user ? (
                <button
                  onClick={login}
                  data-testid="premium-signin-artist-btn"
                  className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition"
                >
                  Sign in to upgrade
                </button>
              ) : tier === "artist" ? (
                <>
                  <button
                    disabled
                    data-testid="premium-artist-current-btn"
                    className="bg-white/15 text-white rounded-full px-6 py-2.5 cursor-default flex items-center justify-center gap-2"
                  >
                    <Check size={16} className="text-[#E2FF31]" /> Your current plan
                  </button>
                  <button
                    data-testid="premium-downgrade-listener-btn"
                    onClick={() => subscribe("premium")}
                    disabled={loading}
                    className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    ↓ Switch to Listener · $10/mo
                  </button>
                </>
              ) : tier === "premium" ? (
                !status?.trial_used_artist ? (
                  <button
                    data-testid="premium-upgrade-artist-trial-btn"
                    onClick={() => startTrial("artist")}
                    disabled={loading}
                    className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Sparkles size={16} className="text-[#E2FF31]" />
                    {loading ? "Starting…" : "Start 3-month free Artist trial"}
                  </button>
                ) : (
                  <button
                    data-testid="premium-upgrade-artist-btn"
                    onClick={() => subscribe("artist")}
                    disabled={loading}
                    className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Sparkles size={16} className="text-[#E2FF31]" /> Upgrade to Artist · $25/mo
                  </button>
                )
              ) : !status?.trial_used_artist && !isPremiumActive ? (
                <>
                  <button
                    data-testid="premium-start-artist-trial-btn"
                    onClick={() => startTrial("artist")}
                    disabled={loading}
                    className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Mic size={16} />
                    {loading ? "Starting…" : "Start 3-month free trial"}
                  </button>
                  <button
                    data-testid="premium-subscribe-artist-btn"
                    onClick={() => subscribe("artist")}
                    disabled={loading}
                    className="bg-white/8 hover:bg-white/15 text-white rounded-full px-6 py-2.5 transition text-sm"
                  >
                    Skip trial — $25/month
                  </button>
                </>
              ) : (
                <button
                  data-testid="premium-subscribe-artist-btn"
                  onClick={() => subscribe("artist")}
                  disabled={loading}
                  className="bg-[#FF2E00] hover:bg-[#E62900] text-white font-semibold rounded-full px-6 py-2.5 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Crown size={16} />
                  Become an Artist · $25/mo
                </button>
              )}
            </div>
            <p className="text-xs text-white/40 mt-3">Cancel any time. Includes everything in Listener Premium.</p>
          </div>
        </div>
      </div>

      {/* Free vs Premium vs Artist compare */}
      <div className="rounded-3xl p-6 sm:p-8 border border-white/10 bg-white/[0.03] mb-12">
        <h3 className="font-display text-2xl mb-5">Compare plans</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 text-xs uppercase tracking-wider">
              <th className="text-left pb-3">Feature</th>
              <th className="pb-3 text-center">Free</th>
              <th className="pb-3 text-center">Listener · $10</th>
              <th className="pb-3 text-center">Artist · $25</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {[
              ["Browse & search", true, true, true],
              ["30s previews + full-length playback", true, true, true],
              ["Unlimited playlists", false, true, true],
              ["Ad-free experience", false, true, true],
              ["Premium badge", false, true, true],
              ["High-quality previews", false, true, true],
              ["Artist Studio (MP3 uploads)", false, false, true],
              ["Public artist profile + bio", false, false, true],
              ["Verified Artist badge", false, false, true],
              ["AI Release Tracker priority", false, false, true],
            ].map(([label, free, prem, art]) => (
              <tr key={label}>
                <td className="py-3 text-white/80">{label}</td>
                <td className="text-center">
                  {free ? <Check size={16} className="inline text-white/50" /> : <span className="text-white/20">—</span>}
                </td>
                <td className="text-center">
                  {prem ? <Check size={16} className="inline text-white/80" /> : <span className="text-white/20">—</span>}
                </td>
                <td className="text-center">
                  {art ? <Check size={16} className="inline text-[#E2FF31]" /> : <span className="text-white/20">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={() => navigate("/")}
        className="text-sm text-white/50 hover:text-white"
      >
        ← Back to home
      </button>
    </div>
  );
};

export default Premium;
